import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as Logo } from "../assets/logo.svg";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const toggleMobileMenu = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close menu if click is outside menu and not on menu button
      if (
        menuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-secondary backdrop-blur-md border-b border-primary/20 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <Logo className="w-10 h-10" />
            <div className="flex flex-col items-start">
              <span className="text-primary logoText font-semibold text-xl">
                MockingJay
              </span>
              <span className="text-gray-200 text-xs">WeBuildWhatYouNeed</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center text-accent gap-8">
            {/* HOME — visible by default (text-accent) + underline always */}
            <Link
              to="/"
              className="text-accent hover:text-primary font-medium transition-colors duration-200 relative"
            >
              Home
              <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary"></span>
            </Link>

            <Link
              to="/blog"
              className=" hover:text-primary font-medium transition-colors duration-200 relative group"
            >
              Blog
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
            </Link>

            <Link
              to="/book-demo"
              className=" hover:text-primary font-medium transition-colors duration-200 relative group"
            >
              Book Demo
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
            </Link>

            <Link
              to="/careers"
              className=" hover:text-primary font-medium transition-colors duration-200 relative group"
            >
              Careers
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
            </Link>

            {/* LOGIN — default hidden, ONLY login pe hover ho tab visible */}
            <Link
              to="/login"
              className="opacity-0 hover:opacity-100 transition-opacity duration-300
                         px-4 py-2 rounded-lg border border-primary text-primary
                         hover:bg-primary/90 hover:text-secondary hover:border-primary"
            >
              Login
            </Link>
          </nav>

          {/* CTA Button and Mobile Menu */}
          <div className="flex items-center gap-3">
            {!isHomePage && (
              <Link
                to="/contact"
                className="hidden sm:inline-flex items-center px-4 py-2 rounded-lg border border-accent text-accent"
              >
                Contact Us
              </Link>
            )}
            {isHomePage && (
              <a
                href="#contact"
                className="hidden sm:inline-flex items-center px-4 py-2 rounded-lg border border-accent text-accent font-medium hover:bg-primary/90 hover:text-secondary hover:border-primary transition-colors duration-200"
              >
                Contact Us
              </a>
            )}

            {/* Mobile Menu Button */}
            <button
              ref={menuButtonRef}
              onClick={toggleMobileMenu}
              className="md:hidden p-2 rounded-lg text-accent hover:text-primary hover:bg-primary/10 transition-colors duration-200"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              {menuOpen ? (
                <CloseIcon className="w-6 h-6" />
              ) : (
                <MenuIcon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {menuOpen && (
          <div
            ref={mobileMenuRef}
            className="md:hidden text-accent text-base font-medium border-t border-primary/20 bg-secondary/95 backdrop-blur-md"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className="block px-3 py-2 rounded-md hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                onClick={() => setMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/blog"
                className="block px-3 py-2 rounded-md hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                onClick={() => setMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/book-demo"
                className="block px-3 py-2 rounded-md hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                onClick={() => setMenuOpen(false)}
              >
                Book Demo
              </Link>
              <Link
                to="/careers"
                className="block px-3 py-2 rounded-md hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                onClick={() => setMenuOpen(false)}
              >
                Careers
              </Link>
              <Link
                to="/contact"
                className="block px-3 py-2 rounded-md hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                onClick={() => setMenuOpen(false)}
              >
                Contact Us
              </Link>
              <Link
                to="/login"
                className="block px-3 py-2 rounded-md hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                onClick={() => setMenuOpen(false)}
              >
                Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
